import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ShellHeader from '../components/ShellHeader';
import AddVehicleSection from '../components/admin/AddVehicleSection';
import AdminTabs from '../components/admin/AdminTabs';
import InventorySection from '../components/admin/InventorySection';
import TestimonialsSection from '../components/admin/TestimonialsSection';
import UsersSection from '../components/admin/UsersSection';
import { useVehicles } from '../context/VehiclesContext';
import { auth } from '../firebase';
import { compressImages } from '../utils/compressImage';

const ADMIN_SECTIONS = ['inventory', 'add-vehicle', 'testimonials', 'users'];
const INVENTORY_PAGE_SIZE = 4;

function calculateFinancePrice(salePrice) {
  if (!salePrice || isNaN(salePrice)) return '';
  const principal = parseFloat(salePrice);
  const totalAmount = principal * 1.15; // 15% interest added to principal
  const monthlyPayment = totalAmount / 36; // 3 years = 36 months
  return monthlyPayment.toFixed(2);
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(email || '').trim());
}

function AdminPage({ authUser }) {
  const location = useLocation();
  const { refreshVehicles } = useVehicles();
  const [activeSection, setActiveSection] = useState('inventory');
  const [vehicles, setVehicles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [approvedTestimonials, setApprovedTestimonials] = useState([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryPage, setInventoryPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);
  const [approvedTestimonialsLoading, setApprovedTestimonialsLoading] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [addManufacturerSubmitting, setAddManufacturerSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadForm, setUploadForm] = useState({
    make: '',
    model: '',
    year: '',
    mileage: '',
    vehicleType: '',
    fuelType: '',
    gearboxType: '',
    engineSize: '',
    registrationNumber: '',
    salePrice: '',
    financePrice: '',
    description: '',
  });
  const [uploadImages, setUploadImages] = useState([]);
  const [photoPickerKey, setPhotoPickerKey] = useState(0);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [usersMessage, setUsersMessage] = useState('');
  const [usersSubmitting, setUsersSubmitting] = useState(false);
  const [privilegesLoading, setPrivilegesLoading] = useState(false);
  const [privilegesSubmitting, setPrivilegesSubmitting] = useState(false);
  const [privilegesMessage, setPrivilegesMessage] = useState('');
  const [privilegeUsersLoading, setPrivilegeUsersLoading] = useState(false);
  const [privilegeUsers, setPrivilegeUsers] = useState([]);
  const [myPrivileges, setMyPrivileges] = useState({
    admin: false,
    mainAdmin: false,
    canManagePrivileges: false,
  });

  const imagePreviews = useMemo(
    () => uploadImages.map((file) => ({ name: file.name, url: URL.createObjectURL(file) })),
    [uploadImages]
  );

  const filteredInventoryVehicles = useMemo(() => {
    const search = inventorySearch.trim().toLowerCase();
    if (!search) {
      return vehicles;
    }

    return vehicles.filter((vehicle) => {
      const make = String(vehicle.make || '').toLowerCase();
      const model = String(vehicle.model || '').toLowerCase();
      const registrationNumber = String(vehicle.registrationNumber || '').toLowerCase();
      const fullName = `${make} ${model}`.trim();

      return (
        make.includes(search) ||
        model.includes(search) ||
        registrationNumber.includes(search) ||
        fullName.includes(search)
      );
    });
  }, [vehicles, inventorySearch]);

  const totalInventoryPages = Math.max(
    1,
    Math.ceil(filteredInventoryVehicles.length / INVENTORY_PAGE_SIZE)
  );

  const paginatedInventoryVehicles = useMemo(() => {
    const start = (inventoryPage - 1) * INVENTORY_PAGE_SIZE;
    return filteredInventoryVehicles.slice(start, start + INVENTORY_PAGE_SIZE);
  }, [filteredInventoryVehicles, inventoryPage]);

  useEffect(() => () => {
    imagePreviews.forEach((image) => URL.revokeObjectURL(image.url));
  }, [imagePreviews]);

  useEffect(() => {
    setInventoryPage(1);
  }, [inventorySearch]);

  useEffect(() => {
    setInventoryPage((current) => Math.min(current, totalInventoryPages));
  }, [totalInventoryPages]);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/vehicles');
      if (!response.ok) {
        throw new Error('Failed to load vehicles.');
      }
      const data = await response.json();
      setVehicles(data);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTestimonials = async () => {
    try {
      setTestimonialsLoading(true);
      const token = await auth.currentUser.getIdToken();
      const response = await fetch('/api/testimonials/pending', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load pending testimonials.');
      }
      const data = await response.json();
      setTestimonials(data || []);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setTestimonialsLoading(false);
    }
  };

  const loadManufacturers = async () => {
    try {
      const response = await fetch('/api/manufacturers');
      if (!response.ok) {
        throw new Error('Failed to load manufacturers.');
      }

      const payload = await response.json();
      const manufacturerList = Array.isArray(payload.manufacturers) ? payload.manufacturers : [];
      setManufacturers(manufacturerList);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const loadApprovedTestimonials = useCallback(async () => {
    try {
      setApprovedTestimonialsLoading(true);
      if (!auth.currentUser) {
        throw new Error('Please log in again.');
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch('/api/testimonials?approved=true&limit=200', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load approved testimonials.');
      }
      const data = await response.json();
      setApprovedTestimonials(data || []);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setApprovedTestimonialsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVehicles();
    loadTestimonials();
    loadManufacturers();
  }, []);

  useEffect(() => {
    if (activeSection === 'testimonials') {
      loadApprovedTestimonials();
    }
  }, [activeSection, loadApprovedTestimonials]);

  useEffect(() => {
    const section = new URLSearchParams(location.search).get('section');
    if (section && ADMIN_SECTIONS.includes(section)) {
      setActiveSection(section);
    }
  }, [location.search]);

  const handleUploadInputChange = (event) => {
    const { name, value } = event.target;
    setUploadForm((current) => {
      const updated = { ...current, [name]: value };
      if (name === 'salePrice') {
        updated.financePrice = calculateFinancePrice(value);
      }
      return updated;
    });
  };

  const authorizedFetch = async (url, options = {}) => {
    if (!auth.currentUser) {
      throw new Error('Please log in again.');
    }

    const token = await auth.currentUser.getIdToken();

    return fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const loadMyPrivileges = useCallback(async () => {
    try {
      setPrivilegesLoading(true);
      const response = await authorizedFetch('/api/privileges/me');
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.error || 'Unable to load your privileges.');
      }

      setMyPrivileges({
        admin: payload.admin === true,
        mainAdmin: payload.mainAdmin === true,
        canManagePrivileges: payload.canManagePrivileges === true,
      });
    } catch (error) {
      setPrivilegesMessage(error.message);
    } finally {
      setPrivilegesLoading(false);
    }
  }, []);

  const loadPrivilegeUsers = useCallback(async () => {
    try {
      setPrivilegeUsersLoading(true);
      const response = await authorizedFetch('/api/privileges/users');
      const payload = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          setPrivilegeUsers([]);
          return;
        }

        throw new Error(payload.error || 'Unable to load users for privileges.');
      }

      setPrivilegeUsers(Array.isArray(payload.users) ? payload.users : []);
    } catch (error) {
      setPrivilegesMessage(error.message);
      setPrivilegeUsers([]);
    } finally {
      setPrivilegeUsersLoading(false);
    }
  }, []);

  const handleUpload = async (event) => {
    event.preventDefault();

    const formData = new FormData();
    Object.entries(uploadForm).forEach(([key, value]) => {
      formData.append(key, value);
    });

    if (uploadImages.length === 0) {
      setMessage('Please add at least one photo.');
      return;
    }

    try {
      setMessage('Preparing photos...');
      const compressed = await compressImages(uploadImages);
      compressed.forEach((image) => {
        formData.append('images', image);
      });

      const response = await authorizedFetch('/api/vehicles', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload.error || 'Upload failed.');
      }

      setMessage('Vehicle uploaded successfully.');
      setUploadForm({
        make: '',
        model: '',
        year: '',
        mileage: '',
        vehicleType: '',
        fuelType: '',
        gearboxType: '',
        engineSize: '',
        registrationNumber: '',
        salePrice: '',
        financePrice: '',
        description: '',
      });
      setUploadImages([]);
      setPhotoPickerKey((current) => current + 1);
      await Promise.all([loadVehicles(), refreshVehicles()]);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handleAddManufacturer = async (rawName) => {
    const name = String(rawName || '').trim();

    if (name.length < 2) {
      setMessage('Please enter at least 2 characters for manufacturer name.');
      return;
    }

    const alreadyExists = manufacturers.some(
      (manufacturer) => manufacturer.trim().toLowerCase() === name.toLowerCase()
    );

    if (alreadyExists) {
      setMessage('Manufacturer already exists in the list.');
      return;
    }

    try {
      setAddManufacturerSubmitting(true);

      const response = await authorizedFetch('/api/manufacturers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      });

      const payload = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have admin privileges to add manufacturers.');
        }

        throw new Error(payload.error || 'Could not add manufacturer.');
      }

      const resolvedName = String(payload.name || name).trim();

      setManufacturers((current) => {
        const next = [...current];
        if (!next.some((item) => item.trim().toLowerCase() === resolvedName.toLowerCase())) {
          next.push(resolvedName);
          next.sort((a, b) => a.localeCompare(b));
        }
        return next;
      });

      setUploadForm((current) => ({
        ...current,
        make: resolvedName,
      }));

      setMessage(
        payload.created === false
          ? `Manufacturer already existed: ${resolvedName}`
          : `Manufacturer added: ${resolvedName}`
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setAddManufacturerSubmitting(false);
    }
  };

  const toggleSold = async (vehicle) => {
    try {
      const response = await authorizedFetch(`/api/vehicles/${vehicle.id}/sold`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isSold: !vehicle.isSold }),
      });

      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload.error || 'Could not update sold status.');
      }

      setMessage(`Vehicle marked as ${vehicle.isSold ? 'available' : 'sold'}.`);
      await Promise.all([loadVehicles(), refreshVehicles()]);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const deleteSoldVehicle = async (vehicle) => {
    if (!vehicle?.id) {
      setMessage('Invalid vehicle selected.');
      return;
    }

    if (!vehicle.isSold) {
      setMessage('Only sold vehicles can be deleted.');
      return;
    }

    const confirmed = window.confirm(
      `Delete sold vehicle ${vehicle.make || ''} ${vehicle.model || ''}? This cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      const response = await authorizedFetch(`/api/vehicles/${vehicle.id}`, {
        method: 'DELETE',
      });

      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.error || 'Could not delete sold vehicle.');
      }

      setMessage('Sold vehicle deleted.');
      await Promise.all([loadVehicles(), refreshVehicles()]);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const approveTestimonial = async (id) => {
    try {
      const response = await authorizedFetch(`/api/testimonials/${id}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload.error || 'Could not approve testimonial.');
      }

      setMessage('Testimonial approved and published.');
      await Promise.all([loadTestimonials(), loadApprovedTestimonials()]);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      const response = await authorizedFetch(`/api/testimonials/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload.error || 'Could not delete testimonial.');
      }

      setMessage('Testimonial deleted.');
      await loadTestimonials();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const deleteApprovedTestimonial = async (id) => {
    try {
      const response = await authorizedFetch(`/api/testimonials/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload.error || 'Could not delete approved testimonial.');
      }

      setMessage('Approved testimonial deleted.');
      await loadApprovedTestimonials();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handleUserInputChange = (event) => {
    const { name, value } = event.target;
    setUserForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const validateUserForm = () => {
    const email = userForm.email.trim().toLowerCase();

    if (!email || !userForm.password || !userForm.confirmPassword) {
      return 'Please complete all user fields.';
    }

    if (!isValidEmail(email)) {
      return 'Please enter a valid email address.';
    }

    if (userForm.password.length < 8) {
      return 'Password must be at least 8 characters.';
    }

    if (userForm.password !== userForm.confirmPassword) {
      return 'Passwords do not match.';
    }

    return '';
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setUsersMessage('');

    const validationError = validateUserForm();
    if (validationError) {
      setUsersMessage(validationError);
      return;
    }

    try {
      setUsersSubmitting(true);

      const response = await authorizedFetch('/api/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userForm.email.trim().toLowerCase(),
          password: userForm.password,
          confirmPassword: userForm.confirmPassword,
        }),
      });

      const payload = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('You do not have admin privileges to create users.');
        }

        throw new Error(payload.error || 'Could not create user.');
      }

      setUsersMessage(`User created: ${payload.email}`);
      setUserForm({
        email: '',
        password: '',
        confirmPassword: '',
      });
    } catch (error) {
      setUsersMessage(error.message);
    } finally {
      setUsersSubmitting(false);
    }
  };

  const handleToggleAdminPrivilege = async (targetUser) => {
    setPrivilegesMessage('');

    if (!targetUser?.uid) {
      setPrivilegesMessage('Please select a valid user.');
      return;
    }

    if (targetUser.mainAdmin) {
      setPrivilegesMessage('Main admin access cannot be changed here.');
      return;
    }

    try {
      setPrivilegesSubmitting(true);

      const response = await authorizedFetch('/api/privileges/admin', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uid: targetUser.uid,
          isAdmin: !targetUser.admin,
        }),
      });

      const payload = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Only the main admin can change privileges.');
        }

        throw new Error(payload.error || 'Unable to update privileges.');
      }

      setPrivilegesMessage(
        `${payload.email} is now ${payload.admin ? 'an admin' : 'not an admin'}.`
      );
      setPrivilegeUsers((current) =>
        current.map((user) =>
          user.uid === payload.uid ? { ...user, admin: payload.admin === true } : user
        )
      );
    } catch (error) {
      setPrivilegesMessage(error.message);
    } finally {
      setPrivilegesSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeSection === 'users') {
      loadMyPrivileges();
      loadPrivilegeUsers();
    }
  }, [activeSection, loadMyPrivileges, loadPrivilegeUsers]);

  return (
    <div className="site-shell">
      <ShellHeader authUser={authUser} />

      <AdminTabs activeSection={activeSection} setActiveSection={setActiveSection} />

      <main className="content-wrap">
        {activeSection === 'inventory' && (
          <InventorySection
            loading={loading}
            message={message}
            inventorySearch={inventorySearch}
            setInventorySearch={setInventorySearch}
            paginatedInventoryVehicles={paginatedInventoryVehicles}
            filteredInventoryVehicles={filteredInventoryVehicles}
            inventoryPage={inventoryPage}
            totalInventoryPages={totalInventoryPages}
            pageSize={INVENTORY_PAGE_SIZE}
            setInventoryPage={setInventoryPage}
            toggleSold={toggleSold}
            deleteSoldVehicle={deleteSoldVehicle}
          />
        )}

        {activeSection === 'add-vehicle' && (
          <AddVehicleSection
            message={message}
            handleUpload={handleUpload}
            uploadForm={uploadForm}
            handleUploadInputChange={handleUploadInputChange}
            manufacturerOptions={manufacturers}
            addManufacturerSubmitting={addManufacturerSubmitting}
            onAddManufacturer={handleAddManufacturer}
            photoPickerKey={photoPickerKey}
            setUploadImages={setUploadImages}
            setPhotoPickerKey={setPhotoPickerKey}
            uploadImages={uploadImages}
            imagePreviews={imagePreviews}
          />
        )}

        {activeSection === 'testimonials' && (
          <TestimonialsSection
            testimonials={testimonials}
            testimonialsLoading={testimonialsLoading}
            approveTestimonial={approveTestimonial}
            deleteTestimonial={deleteTestimonial}
            approvedTestimonials={approvedTestimonials}
            approvedTestimonialsLoading={approvedTestimonialsLoading}
            deleteApprovedTestimonial={deleteApprovedTestimonial}
          />
        )}

        {activeSection === 'users' && (
          <UsersSection
            privilegesLoading={privilegesLoading}
            myPrivileges={myPrivileges}
            usersMessage={usersMessage}
            handleCreateUser={handleCreateUser}
            userForm={userForm}
            handleUserInputChange={handleUserInputChange}
            usersSubmitting={usersSubmitting}
            privilegesMessage={privilegesMessage}
            privilegeUsers={privilegeUsers}
            privilegeUsersLoading={privilegeUsersLoading}
            privilegesSubmitting={privilegesSubmitting}
            handleToggleAdminPrivilege={handleToggleAdminPrivilege}
          />
        )}
      </main>
    </div>
  );
}

export default AdminPage;
